import { useMemo, Fragment } from "react";

const RANK = { S: 0, "1": 1, "2": 2, "3": 3 };
const met = (a, r) => a != null && RANK[a] != null && RANK[a] <= (RANK[String(r).trim()] ?? 3);

// Read-only progress monitor: criteria (rows) × lessons (columns), colour-coded achieved vs required.
export default function PlanningMatrix({ program, records }) {
  const lessons = useMemo(
    () => (program.lessons || []).slice().sort((a, b) => a.seq - b.seq),
    [program.lessons]
  );

  // Build unique criterion rows grouped by element, keyed by criterion ref (fallback to text).
  const groups = useMemo(() => {
    const byElement = new Map();
    lessons.forEach((l) => {
      (l.assessment_elements || []).forEach((el) => {
        const ekey = el.mos_ref || el.title || "General";
        if (!byElement.has(ekey)) byElement.set(ekey, { key: ekey, title: el.title, mos_ref: el.mos_ref, rows: new Map() });
        const grp = byElement.get(ekey);
        (el.criteria || []).forEach((c) => {
          const ckey = c.ref || c.text;
          if (!grp.rows.has(ckey)) grp.rows.set(ckey, { key: ckey, ref: c.ref, text: c.text, cells: {} });
          grp.rows.get(ckey).cells[l.id] = { critId: c.id, required: c.required };
        });
      });
    });
    return [...byElement.values()].map((g) => ({ ...g, rows: [...g.rows.values()] }));
  }, [lessons]);

  if (groups.length === 0) return <p className="text-sm text-slate-500">This program has no assessment criteria to plot. Import a syllabus with an MOS grid.</p>;

  const cell = (row, lesson) => {
    const c = row.cells[lesson.id];
    if (!c) return <td key={lesson.id} className="border border-slate-100 bg-slate-50/50" />;
    const achieved = records[lesson.id]?.achieved?.[c.critId];
    let cls = "bg-slate-100 text-slate-400", txt = c.required;
    if (achieved) {
      cls = met(achieved, c.required) ? "bg-lime-200 text-lime-800 font-semibold" : "bg-amber-200 text-amber-800 font-semibold";
      txt = achieved;
    }
    return <td key={lesson.id} className={`border border-white text-center text-xs px-2 py-1.5 ${cls}`} title={`${row.text} — required ${c.required}${achieved ? `, achieved ${achieved}` : ""}`} data-testid={`matrix-cell-${lesson.id}-${row.key}`}>{txt}</td>;
  };

  return (
    <div data-testid={`planning-matrix-${program.id}`}>
      <div className="flex flex-wrap items-center gap-3 mb-3 text-xs text-slate-500">
        <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded bg-slate-100 border border-slate-200" /> Required (not graded)</span>
        <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded bg-lime-200" /> Met (achieved ≤ required)</span>
        <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded bg-amber-200" /> Not yet met</span>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-slate-900 text-white text-left text-xs font-semibold px-3 py-2 min-w-[240px]">Criterion</th>
              {lessons.map((l) => (
                <th key={l.id} className="bg-slate-800 text-white text-[10px] font-medium px-2 py-2 whitespace-nowrap" title={l.title}>
                  <div className="max-w-[90px] truncate">{l.seq}. {l.title}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <Fragment key={g.key}>
                <tr>
                  <td colSpan={lessons.length + 1} className="bg-slate-100 text-slate-600 font-semibold text-xs px-3 py-1.5">
                    {g.mos_ref && <span className="font-mono text-slate-400 mr-1.5">{g.mos_ref}</span>}{g.title}
                  </td>
                </tr>
                {g.rows.map((row) => (
                  <tr key={row.key} className="hover:bg-slate-50">
                    <td className="sticky left-0 z-10 bg-white border border-slate-100 text-xs text-slate-600 px-3 py-1.5 min-w-[240px]">
                      {row.ref && <span className="font-mono text-slate-400 mr-1.5">{row.ref}</span>}{row.text}
                    </td>
                    {lessons.map((l) => cell(row, l))}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
